import { type CSSProperties, type ReactNode, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import DefaultItem from '@/components/Common/DefaultItem';
import SelectDropdown from '@/components/Select/SelectDropdown';
import type { SelectOptionT } from '@/components/Select/types';
import Typo from '@/components/Typography/Typo';
import { NOT_ASSIGNED_LABEL } from '@/configs/reserved-word';
import useColor from '@/hooks/domain/useColor';
import { multilineEllipsis } from '@/styles/style';

import PropertyValue from '../../../components/Typography/PropertyValue';

type ViewT = 'create' | 'detail' | 'list';

type Props = {
  name: string;
  view: ViewT;
  selected: string | null;
  options: SelectOptionT[];
  onSelect: (key: string) => void;
  onClear?: () => void;
  onDelete?: (option: SelectOptionT) => void;
  onCreate?: (optionName: string) => void;
  style?: CSSProperties;
  defaultSelectedStyle?: CSSProperties;
  dropdownMapper?: (value: StringNumberValue) => ReactNode;
};

const AttrSelectDropdown = ({
  name,
  view,
  selected,
  options,
  onSelect,
  onClear,
  onDelete,
  onCreate,
  style,
  dropdownMapper,
}: Props) => {
  const color = useColor();

  const placeholder = view === 'list' ? '-' : NOT_ASSIGNED_LABEL;
  const textType = view === 'list' ? 'label2' : 'label3';

  const defaultValueMapper = useMemo(
    () => (val: StringNumberValue) => {
      return <PropertyValue value={dropdownMapper?.(val) || val} />;
    },
    [dropdownMapper],
  );

  const listMapper = useMemo(
    () => (val: StringNumberValue) => {
      return (
        <DefaultItem>
          <ItemText type={textType} color={color.gray700}>
            {dropdownMapper?.(val) || val}
          </ItemText>
        </DefaultItem>
      );
    },
    [color.gray700, dropdownMapper, textType],
  );

  const defaultPlaceholderMapper = useMemo(
    () => () => {
      return (
        <PropertyValue value={placeholder} style={{ color: color.gray500 }} />
      );
    },
    [color.gray500, placeholder],
  );

  const listPlaceholderMapper = useMemo(
    () => () => {
      return (
        <DefaultItem>
          <ItemText type={textType} color={color.gray500}>
            {placeholder}
          </ItemText>
        </DefaultItem>
      );
    },
    [color.gray500, placeholder, textType],
  );

  const currMapper = useCallback(
    (value: StringNumberValue | null) => {
      if (value) {
        if (view !== 'list') {
          return defaultValueMapper(value);
        }
        return listMapper(value);
      } else {
        if (view !== 'list') {
          return defaultPlaceholderMapper();
        }
        return listPlaceholderMapper();
      }
    },
    [
      defaultPlaceholderMapper,
      defaultValueMapper,
      listMapper,
      listPlaceholderMapper,
      view,
    ],
  );

  return (
    <SelectDropdown
      searchOptions={{
        enable: true,
        placeholder: 'Search existing or add new...',
      }}
      width="200px"
      name={name}
      value={selected}
      data={() => ({
        list: options,
        cursor: null,
      })}
      customMapper={currMapper}
      onSelect={(key) => onSelect(key as string)}
      onClear={onClear}
      onDelete={onDelete}
      onCreate={onCreate}
      placeholder={placeholder}
      style={style}
    />
  );
};

const ItemText = styled(Typo)`
  ${multilineEllipsis(1)};
`;

export default AttrSelectDropdown;
